import type { NotificationRecipient } from '@/lib/services/notification-recipients/notification-recipients.types'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useIsReadOnly } from '@/lib/casl'
import { NotificationChannelType } from '@/lib/enums/NotificationChannelType'
import { useSendRecipientVerificationMutation } from '@/lib/services/notification-recipients/notification-recipients.query'
import { cn, getErrorMessage } from '@/lib/utils'
import { Check, Clock3, RefreshCw, X } from 'lucide-react'
import { toast } from 'sonner'
import { Typography } from '@/components/ui/typography'
const verificationCopyByType = {
  [NotificationChannelType.Email.Value]: {
    actionLabel: 'Resend Verification Email',
    pendingActionLabel: 'Resending Verification Email...',
    pendingTitle: 'Verification email sent',
    pendingDescription:
      'We sent a verification email to this recipient. They need to open the link before they can receive notifications.',
    waitingDescription:
      'This email recipient has not been verified yet. Send another verification when they are ready to confirm it.',
  },
  [NotificationChannelType.Text.Value]: {
    actionLabel: 'Resend Verification Text',
    pendingActionLabel: 'Resending Verification Text...',
    pendingTitle: 'Verification text sent',
    pendingDescription:
      'We sent a verification text to this recipient. They need to open the link before they can receive notifications.',
    waitingDescription:
      'This text recipient has not been verified yet. Send another verification when they are ready to confirm it.',
  },
  [NotificationChannelType.Phone.Value]: {
    actionLabel: 'Place Verification Call',
    pendingActionLabel: 'Placing Verification Call...',
    pendingTitle: 'Verification phone call placed',
    pendingDescription:
      'We placed a verification call to this recipient. They need to press 1 during the call before they can receive notifications.',
    waitingDescription:
      'This phone call recipient has not been verified yet. Send another verification call when they are ready to answer.',
  },
  [NotificationChannelType.Fax.Value]: {
    actionLabel: 'Resend Verification Fax',
    pendingActionLabel: 'Resending Verification Fax...',
    pendingTitle: 'Verification fax sent',
    pendingDescription:
      'We sent a verification fax to this recipient. Verification completes automatically after the fax is delivered.',
    waitingDescription:
      'This fax recipient has not been verified yet. Send another verification fax when they are ready to receive it.',
  },
}

type VerificationRecipient = Pick<
  NotificationRecipient,
  'id' | 'verified_at' | 'has_active_verification'
> & {
  channel: NotificationRecipient['channel'] | null
}

type VerificationSectionProps = { recipient?: VerificationRecipient | null }

export default function VerificationSection({ recipient }: VerificationSectionProps) {
  const { isReadOnly } = useIsReadOnly()
  const sendVerificationMutation = useSendRecipientVerificationMutation()

  const isVerified = recipient?.verified_at != null
  const hasActiveVerification = recipient?.has_active_verification === true
  const channel = recipient?.channel
  const copy = channel == null ? undefined : verificationCopyByType[channel]
  const canVerify = !!copy

  if (!canVerify || !recipient) {
    return null
  }

  const isPendingVerification = !isVerified && hasActiveVerification
  const statusLabel = isVerified
    ? 'Verified'
    : isPendingVerification
      ? copy?.pendingTitle
      : 'Not Verified'
  const description = isVerified
    ? 'This notification recipient has been verified and can receive notifications.'
    : isPendingVerification
      ? copy?.pendingDescription
      : copy?.waitingDescription
  const StatusIcon = isVerified ? Check : isPendingVerification ? Clock3 : X
  const statusVariant = isVerified ? 'success' : isPendingVerification ? 'warning' : 'destructive'

  const handleSendVerification = async () => {
    try {
      const response = await sendVerificationMutation.mutateAsync(recipient.id)
      toast.success(response?.message || 'Verification sent successfully')
    } catch (error) {
      toast.error('Failed to send verification', {
        description: getErrorMessage(error),
      })
    }
  }

  return (
    <Card
      className="@container"
      size={isVerified ? 'sm' : 'default'}
      surface={isVerified ? 'success' : isPendingVerification ? 'warning' : 'default'}
    >
      <CardHeader className="pb-0">
        <div className="flex flex-col gap-3 @md:flex-row @md:items-start @md:justify-between">
          <div className="min-w-0 space-y-1">
            <CardTitle>
              <Typography variant="h4">Verification</Typography>
            </CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>

          <Badge variant={statusVariant} size="choice" shape="control">
            <StatusIcon className="size-4" />
            <span>{statusLabel}</span>
          </Badge>
        </div>
      </CardHeader>
      {!isVerified && (
        <CardContent className="pt-0">
          <Button
            onClick={handleSendVerification}
            disabled={sendVerificationMutation.isPending || isReadOnly}
            variant="outline"
          >
            <RefreshCw
              className={cn('size-4', sendVerificationMutation.isPending && 'animate-spin')}
            />
            {sendVerificationMutation.isPending ? copy.pendingActionLabel : copy.actionLabel}
          </Button>
        </CardContent>
      )}
    </Card>
  )
}
